import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslator } from "@eo-locale/react";
import {
  FORMAT_TEXT_COMMAND,
  TextFormatType,
  $getSelection,
  $isRangeSelection,
  $getRoot,
  UNDO_COMMAND,
  REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND
} from "lexical";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode
} from "@lexical/list";
import { TOGGLE_LINK_COMMAND, $isLinkNode, LinkNode } from "@lexical/link";

import { CLEAR_FORMATTING_COMMAND } from "../commands/clearFormatting";
import { ToolbarPluginProps } from "../types";
import { LinkDialog } from "../components/LinkDialog";
import { useEditorStyles } from "../theme";

interface ToolbarButtonProps {
  buttonKey: string;
  onClick: () => void;
  children: React.ReactNode;
  computeBaseStyle: (active: boolean, disabled: boolean) => React.CSSProperties;
  themeStyles: ReturnType<typeof useEditorStyles>;
  active?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  title?: string;
  toggle?: boolean;
}

const ToolbarButton = ({
  buttonKey,
  active = false,
  disabled = false,
  onClick,
  ariaLabel,
  title,
  children,
  toggle = false,
  computeBaseStyle,
  themeStyles
}: ToolbarButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const baseStyle = useMemo(
    () => computeBaseStyle(active, disabled),
    [computeBaseStyle, active, disabled]
  );

  const computedStyle = useMemo(() => {
    let style = baseStyle;
    if (!disabled && isHovered) {
      style = { ...style, ...themeStyles.toolbarButtonHoverStyles };
    }
    if (!disabled && isFocused) {
      style = { ...style, ...themeStyles.toolbarButtonFocusStyles };
    }
    return style;
  }, [baseStyle, disabled, isFocused, isHovered, themeStyles]);

  return (
    <button
      type="button"
      onClick={onClick}
      style={computedStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={toggle ? active : undefined}
      title={title}
    >
      {children}
    </button>
  );
};

export const ToolbarPlugin = ({
  allowLinks = false,
  toolbarStyles,
  disabled = false,
  onLinkError,
  toolbarLabels
}: ToolbarPluginProps): JSX.Element => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isUnorderedList, setIsUnorderedList] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [currentLinkUrl, setCurrentLinkUrl] = useState("");

  const { translate } = useTranslator();
  const themeStyles = useEditorStyles();

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));

      const node = selection.anchor.getNode();
      const parent = node.getParent();
      setIsLink($isLinkNode(parent) || $isLinkNode(node));

      const topLevelElement = node.getTopLevelElement();
      const isBulletList =
        topLevelElement &&
        $isListNode(topLevelElement) &&
        topLevelElement.getListType() === "bullet";
      setIsUnorderedList(Boolean(isBulletList));
    }
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  useEffect(() => {
    return editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload);
        return false;
      },
      1
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload);
        return false;
      },
      1
    );
  }, [editor]);

  const formatText = (format: TextFormatType) => {
    if (disabled) return;
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const insertList = () => {
    if (disabled) return;
    const command = isUnorderedList
      ? REMOVE_LIST_COMMAND
      : INSERT_UNORDERED_LIST_COMMAND;
    editor.dispatchCommand(command, undefined);
  };

  const clearFormatting = () => {
    if (disabled) return;
    editor.dispatchCommand(CLEAR_FORMATTING_COMMAND, undefined);
  };

  const toggleLink = () => {
    if (disabled || !allowLinks) return;

    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const node = selection.anchor.getNode();
      const linkNode = $isLinkNode(node.getParent())
        ? (node.getParent() as LinkNode)
        : $isLinkNode(node)
          ? (node as LinkNode)
          : null;

      if (linkNode) {
        setCurrentLinkUrl(linkNode.getURL());
        onLinkError?.("");
        setShowLinkDialog(true);
      } else {
        const selectedText = selection.getTextContent().trim();
        const editorText = $getRoot().getTextContent().trim();

        if (!editorText) {
          onLinkError?.(translate("common.linkDialog.errorAddTextFirst"));
        } else if (!selectedText) {
          onLinkError?.(translate("common.linkDialog.errorSelectText"));
        } else {
          setCurrentLinkUrl("");
          onLinkError?.("");
          setShowLinkDialog(true);
        }
      }
    });
  };

  const computeBaseStyle = useCallback(
    (isActive: boolean, isButtonDisabled: boolean) => {
      if (isButtonDisabled)
        return {
          ...themeStyles.toolbarButtonStyles,
          ...themeStyles.toolbarButtonDisabledStyles
        };
      if (isActive)
        return {
          ...themeStyles.toolbarButtonStyles,
          ...themeStyles.toolbarButtonActiveStyles
        };
      return themeStyles.toolbarButtonStyles;
    },
    [themeStyles]
  );

  return (
    <div style={{ ...themeStyles.defaultToolbarStyles, ...toolbarStyles }}>
      <ToolbarButton
        buttonKey="bold"
        active={isBold}
        disabled={disabled}
        onClick={() => formatText("bold")}
        ariaLabel={toolbarLabels?.bold}
        title={toolbarLabels?.boldShortcut}
        toggle
        computeBaseStyle={computeBaseStyle}
        themeStyles={themeStyles}
      >
        <strong>B</strong>
      </ToolbarButton>

      <ToolbarButton
        buttonKey="italic"
        active={isItalic}
        disabled={disabled}
        onClick={() => formatText("italic")}
        ariaLabel={toolbarLabels?.italic}
        title={toolbarLabels?.italicShortcut}
        toggle
        computeBaseStyle={computeBaseStyle}
        themeStyles={themeStyles}
      >
        <em>I</em>
      </ToolbarButton>

      <ToolbarButton
        buttonKey="underline"
        active={isUnderline}
        disabled={disabled}
        onClick={() => formatText("underline")}
        ariaLabel={toolbarLabels?.underline}
        title={toolbarLabels?.underlineShortcut}
        toggle
        computeBaseStyle={computeBaseStyle}
        themeStyles={themeStyles}
      >
        <u>U</u>
      </ToolbarButton>

      <div style={themeStyles.toolbarDividerStyles} />

      <ToolbarButton
        buttonKey="list"
        active={isUnorderedList}
        disabled={disabled}
        onClick={insertList}
        ariaLabel={toolbarLabels?.unorderedList}
        title={toolbarLabels?.unorderedList}
        toggle
        computeBaseStyle={computeBaseStyle}
        themeStyles={themeStyles}
      >
        &#8226; {toolbarLabels?.list}
      </ToolbarButton>

      <div style={themeStyles.toolbarDividerStyles} />

      <ToolbarButton
        buttonKey="undo"
        disabled={disabled || !canUndo}
        onClick={() =>
          !disabled && editor.dispatchCommand(UNDO_COMMAND, undefined)
        }
        ariaLabel={toolbarLabels?.undo}
        title={toolbarLabels?.undoShortcut}
        computeBaseStyle={computeBaseStyle}
        themeStyles={themeStyles}
      >
        ↶
      </ToolbarButton>

      <ToolbarButton
        buttonKey="redo"
        disabled={disabled || !canRedo}
        onClick={() =>
          !disabled && editor.dispatchCommand(REDO_COMMAND, undefined)
        }
        ariaLabel={toolbarLabels?.redo}
        title={toolbarLabels?.redoShortcut}
        computeBaseStyle={computeBaseStyle}
        themeStyles={themeStyles}
      >
        ↷
      </ToolbarButton>

      <div style={themeStyles.toolbarDividerStyles} />

      <ToolbarButton
        buttonKey="clearFormatting"
        disabled={disabled}
        onClick={clearFormatting}
        ariaLabel={toolbarLabels?.clearFormatting}
        title={toolbarLabels?.clearFormatting}
        computeBaseStyle={computeBaseStyle}
        themeStyles={themeStyles}
      >
        {toolbarLabels?.clear}
      </ToolbarButton>

      {allowLinks && (
        <>
          <div style={themeStyles.toolbarDividerStyles} />
          <ToolbarButton
            buttonKey="link"
            active={isLink}
            disabled={disabled}
            onClick={toggleLink}
            ariaLabel={
              isLink ? toolbarLabels?.removeLink : toolbarLabels?.insertLink
            }
            title={
              isLink ? toolbarLabels?.removeLink : toolbarLabels?.insertLink
            }
            toggle
            computeBaseStyle={computeBaseStyle}
            themeStyles={themeStyles}
          >
            🔗
          </ToolbarButton>
        </>
      )}

      {allowLinks && (
        <LinkDialog
          open={showLinkDialog}
          initialUrl={currentLinkUrl}
          labels={{
            insertTitle: translate("common.linkDialog.insertTitle"),
            editTitle: translate("common.linkDialog.editTitle"),
            insertButton: translate("common.linkDialog.insertButton"),
            updateButton: translate("common.linkDialog.updateButton"),
            removeButton: translate("common.linkDialog.removeButton"),
            cancelButton: translate("common.linkDialog.cancelButton")
          }}
          errorMessages={{
            errorUrlEmpty: translate("common.linkDialog.errorUrlEmpty"),
            errorUrlProtocol: translate("common.linkDialog.errorUrlProtocol"),
            errorUrlInvalid: translate("common.linkDialog.errorUrlInvalid")
          }}
          onSubmit={(url) => {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
            setShowLinkDialog(false);
            setCurrentLinkUrl("");
            onLinkError?.("");
          }}
          onCancel={() => {
            setShowLinkDialog(false);
            setCurrentLinkUrl("");
            onLinkError?.("");
          }}
          onRemove={() => {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            setShowLinkDialog(false);
            setCurrentLinkUrl("");
            onLinkError?.("");
          }}
        />
      )}
    </div>
  );
};
